import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, FileDown, Download, LogOut } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { AmbassadorBadge } from "@/components/AmbassadorBadge";
import { PayoutDetailsForm } from "@/components/PayoutDetailsForm";
import { MarketingKit } from "@/components/MarketingKit";
import { LEVELS, eur } from "@/data/ambassadorProgramme";

const STATUS_LABEL = { pending: "Pending (refund period)", on_hold: "Under review", eligible: "Awaiting CSI approval", approved: "Approved", paid: "Paid", rejected: "Rejected", void: "Void (refund/chargeback)", blocked: "Not eligible" };

const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copied"));

const Stat = ({ label, value, testid }) => (
  <div className="card-warm p-4" data-testid={testid}><div className="text-xs text-zinc-500">{label}</div><div className="font-display text-2xl mt-1">{value}</div></div>
);

const Pending = ({ a }) => (
  <div className="card-warm p-6" data-testid="ambassador-status-card">
    <h2 className="font-display text-2xl">Application status: <span className="capitalize">{a.status}</span></h2>
    <p className="text-sm text-zinc-700 mt-2">{a.status === "pending" ? "Thank you for applying. CSI reviews every application and will email you within 5 working days." :
      a.status === "rejected" ? "Your application was not approved at this time. You are welcome to re-apply later." : "Your ambassador account is not active."}</p>
    {["rejected", "withdrawn"].includes(a.status) && <Link to="/ambassadors/apply" className="chip text-sm mt-4 inline-block" data-testid="ambassador-reapply">Apply again</Link>}
  </div>
);

const Share = ({ a }) => (
  <div className="card-warm p-5 space-y-3" data-testid="ambassador-share">
    <div className="text-xs text-zinc-500">Ambassador ID</div><div className="font-display text-xl -mt-2" data-testid="ambassador-id">{a.ambassador_id}</div>
    {[["Referral link", a.referral_link, "ambassador-link"], ["Discount code (5% off CSCE & CSP)", a.code, "ambassador-code"]].map(([l, v, t]) => (
      <div key={t}><div className="text-xs text-zinc-500 mb-1">{l}</div>
        <div className="flex gap-2"><input readOnly value={v} className="field flex-1 min-w-0 text-sm" data-testid={t}/><button type="button" onClick={() => copy(v)} className="chip text-xs" data-testid={`${t}-copy`}><Copy size={12}/></button></div>
      </div>
    ))}
  </div>
);

const Commissions = ({ rows }) => (
  <div className="card-warm overflow-x-auto" data-testid="ambassador-commissions">
    <table className="w-full text-sm min-w-[620px]">
      <thead className="bg-[var(--muted-surface)] text-left"><tr><th className="p-3">Date</th><th className="p-3">Programme</th><th className="p-3">Paid</th><th className="p-3">Rate</th><th className="p-3">Commission</th><th className="p-3">Status</th></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={6} className="p-4 text-zinc-500 text-center">No referral enrolments yet - share your link to get started.</td></tr>}
        {rows.map((c) => (
          <tr key={c.id} className="border-t border-[var(--border-warm)]">
            <td className="p-3">{c.created_at.slice(0, 10)}</td><td className="p-3">{c.programme}</td><td className="p-3">{eur(c.amount_paid)}</td>
            <td className="p-3 capitalize">{c.rate_tier}</td><td className="p-3 font-semibold">{eur(c.commission_amount)}</td>
            <td className="p-3 text-xs">{STATUS_LABEL[c.status] || c.status}{c.status === "pending" && <div className="text-zinc-500">eligible {c.eligible_at.slice(0, 10)}</div>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Payouts = ({ rows }) => (
  <div className="card-warm overflow-x-auto" data-testid="ambassador-payouts">
    <table className="w-full text-sm min-w-[520px]">
      <thead className="bg-[var(--muted-surface)] text-left"><tr><th className="p-3">Week</th><th className="p-3">Enrolments</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Wise reference</th></tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={5} className="p-4 text-zinc-500 text-center">No payouts yet.</td></tr>}
        {rows.map((p) => (
          <tr key={p.id} className="border-t border-[var(--border-warm)]"><td className="p-3">{p.period}</td><td className="p-3">{p.count}</td><td className="p-3 font-semibold">{eur(p.total)}</td>
            <td className="p-3 capitalize">{p.status === "paid" ? `Paid ${p.paid_at?.slice(0, 10)}` : `Scheduled (Friday ${p.payout_date})`}</td><td className="p-3">{p.wise_reference || "-"}</td></tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Active = ({ data, reload }) => {
  const a = data.ambassador, s = data.stats;
  const lvl = LEVELS.find((l) => l.name === s.level);
  const withdraw = async () => {
    if (!window.confirm("Withdraw from the Ambassador Programme? Your link and code will stop working.")) return;
    try { await api.post("/ambassadors/me/withdraw"); toast.success("You have withdrawn from the programme"); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const exportData = async () => {
    const { data: d } = await api.get("/ambassadors/me/export");
    Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([JSON.stringify(d, null, 2)], { type: "application/json" })), download: "csi-ambassador-data.json" }).click();
  };
  return (
    <div className="space-y-8">
      {a.status !== "active" && <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800" data-testid="ambassador-inactive-banner">Your ambassador account is {a.status}. Your link and code are inactive.</div>}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3 content-start">
          <Stat label="Total referrals" value={s.total_referrals} testid="stat-referrals"/>
          <Stat label="Total enrolments" value={s.enrolments} testid="stat-enrolments"/>
          <Stat label="CSCE enrolments" value={s.csce_enrolments} testid="stat-csce"/>
          <Stat label="CSP enrolments" value={s.csp_enrolments} testid="stat-csp"/>
          <Stat label="Revenue generated" value={eur(s.revenue)} testid="stat-revenue"/>
          <Stat label="Pending commission" value={eur(s.pending)} testid="stat-pending"/>
          <Stat label="Approved commission" value={eur(s.approved)} testid="stat-approved"/>
          <Stat label="Paid commission" value={eur(s.paid)} testid="stat-paid"/>
          <div className="col-span-2 sm:col-span-4 card-warm p-4 flex flex-wrap items-center gap-4" data-testid="ambassador-level">
            <span className="px-3 py-1 rounded-full text-white text-xs font-bold uppercase tracking-wider" style={{ background: lvl?.color || "#71717a" }}>{s.level}</span>
            <span className="text-sm">{s.enrolments_12m} paid enrolments in the last 12 months · <b className="capitalize">{s.rate_tier}</b> rate{s.to_enhanced > 0 ? ` · ${s.to_enhanced} more for the Enhanced rate` : ""}</span>
            <span className="text-xs text-zinc-500">{s.leads} unique visits · {s.registrations} registrations · next payout {data.next_payout}</span>
          </div>
        </div>
        <Share a={a}/>
      </div>
      {a.status === "active" && <MarketingKit ambassador={a}/>}
      <section><h2 className="font-display text-2xl mb-3">Commissions</h2><Commissions rows={s.commissions}/></section>
      <section><h2 className="font-display text-2xl mb-3">Payment history</h2><Payouts rows={data.payouts}/></section>
      <section className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-warm p-5"><h2 className="font-display text-2xl mb-3">Wise payout details</h2><PayoutDetailsForm initial={a.payout_details} onSaved={reload}/></div>
        <div className="card-warm p-5 space-y-4">
          <h2 className="font-display text-2xl">Certificate and badge</h2>
          {a.status === "active" && <AmbassadorBadge ambassador={a} level={s.level}/>}
          {a.status === "active" && <a href={`${API}/ambassadors/me/certificate.pdf`} className="chip text-xs inline-flex items-center gap-1" data-testid="ambassador-certificate-download"><FileDown size={12}/> Ambassador Certificate (PDF)</a>}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!a.directory_opt_in} onChange={async (e) => { await api.put("/ambassadors/me/directory", { directory_opt_in: e.target.checked }); reload(); }} data-testid="ambassador-directory-toggle"/> List me in the Ambassador Directory</label>
          <div className="flex flex-wrap gap-2 pt-2 border-t border-[var(--border-warm)]">
            <button type="button" onClick={exportData} className="chip text-xs inline-flex items-center gap-1" data-testid="ambassador-export-data"><Download size={12}/> Download my data</button>
            {a.status === "active" && <button type="button" onClick={withdraw} className="chip text-xs inline-flex items-center gap-1 text-red-700" data-testid="ambassador-withdraw"><LogOut size={12}/> Withdraw</button>}
          </div>
        </div>
      </section>
    </div>
  );
};

export default function AmbassadorDashboardPage() {
  useSeo({ title: "Ambassador Dashboard", path: "/ambassador", noindex: true });
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/ambassadors/me").then((r) => setData(r.data)).catch(() => setData({ ambassador: null })), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="p-12 text-center text-sm text-zinc-500">Loading…</div>;
  const a = data.ambassador;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="ambassador-dashboard">
      <div className="eyebrow mb-1">CSI Ambassador</div>
      <h1 className="font-display text-4xl sm:text-5xl mb-6">Ambassador Dashboard</h1>
      {!a ? (
        <div className="card-warm p-6" data-testid="ambassador-none"><p className="text-sm">You are not a CSI Ambassador yet.</p><Link to="/ambassadors" className="btn-terracotta px-5 py-2 rounded-full text-sm font-semibold inline-block mt-4">Learn about the programme</Link></div>
      ) : data.stats ? <Active data={data} reload={load}/> : <Pending a={a}/>}
    </div>
  );
}
